// Aufgaben, Dokumente und Kommunikation.
//
// Drei Listen, die sich Bauform und Bezugsauswahl teilen: Jeder Eintrag hängt
// an einem Objekt, einer Einheit — oder an nichts, dann gilt er portfolioweit.

import { useState, type ReactNode } from "react";
import { api } from "../api.ts";
import { datum, dateigroesse, heute } from "../formate.ts";
import { Kpi, Leer, Sheet, TextFeld, WahlFeld, TextflaecheFeld, ausListe, ausRecord }
  from "../komponenten/Bausteine.tsx";
import { AnhangWaehlen, type Vorgemerkt } from "../komponenten/Anhang.tsx";
import { useBestand, useZustand } from "../zustand.tsx";
import {
  DOKUMENT_ARTEN, KANAELE, PRIO_TEXT, WIEDERHOLUNGEN,
  type Aufgabe, type Dokument, type Nachricht, type Objekt, type Portfolio,
} from "../typen.ts";

// --------------------------------------------------------------- Gemeinsames

/** Alle wählbaren Bezüge: kein Bezug, jedes Objekt, jede Einheit. */
function bezugOptionen(objekte: Objekt[]): (readonly [string, string])[] {
  const liste: (readonly [string, string])[] = [["", "Portfolioweit (kein Bezug)"] as const];
  for (const o of objekte) {
    liste.push([`objekt:${o.id}`, o.name] as const);
    for (const e of o.einheiten) {
      liste.push([`einheit:${e.id}`, `${o.name} — ${e.bezeichnung}`] as const);
    }
  }
  return liste;
}

const bezugAusFeldern = (objektId: string | null, einheitId: string | null): string =>
  einheitId ? `einheit:${einheitId}` : objektId ? `objekt:${objektId}` : "";

const bezugZuFeldern = (wert: string) => ({
  objekt_id: wert.startsWith("objekt:") ? wert.slice(7) : null,
  einheit_id: wert.startsWith("einheit:") ? wert.slice(8) : null,
});

/** Beschriftung eines Bezugs für die Anzeige. */
function bezugName(objekte: Objekt[], objektId: string | null, einheitId: string | null): string {
  if (einheitId) {
    for (const o of objekte) {
      const e = o.einheiten.find((x) => x.id === einheitId);
      if (e) return `${o.name} · ${e.bezeichnung}`;
    }
  }
  if (objektId) return objekte.find((o) => o.id === objektId)?.name ?? "";
  return "portfolioweit";
}

function SeitenKopf(
  { titel, unterzeile, aufNeu, knopf }:
  { titel: string; unterzeile: string; aufNeu: () => void; knopf: string },
) {
  return (
    <div className="page-head">
      <div><h1>{titel}</h1><p className="sub">{unterzeile}</p></div>
      <button className="btn btn-primary" onClick={aufNeu}>{knopf}</button>
    </div>
  );
}

/** Filterleiste im Stil der Mängelliste. */
function Filter<T extends string>(
  { wert, aendern, optionen }:
  { wert: T; aendern: (w: T) => void; optionen: readonly (readonly [T, string])[] },
) {
  return (
    <div className="seg" style={{ margin: 0 }}>
      {optionen.map(([schluessel, text]) => (
        <button key={schluessel} className={wert === schluessel ? "on" : ""}
          onClick={() => aendern(schluessel)}>{text}</button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ Aufgaben

function AufgabeBlatt(
  { aufgabe, aufSchliessen }: { aufgabe: Aufgabe | null; aufSchliessen: () => void },
) {
  const bestand = useBestand();
  const { aendern, zeigeMeldung } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);
  const [f, setzeF] = useState({
    titel: aufgabe?.titel ?? "",
    beschreibung: aufgabe?.beschreibung ?? "",
    prio: aufgabe?.prio ?? "mittel",
    faellig: aufgabe?.faellig ?? "",
    zustaendig: aufgabe?.zustaendig ?? "",
    wiederholung: aufgabe?.wiederholung ?? "keine",
    bezug: bezugAusFeldern(aufgabe?.objekt_id ?? null, aufgabe?.einheit_id ?? null),
  });
  const setze = <K extends keyof typeof f>(k: K) => (w: typeof f[K]) => setzeF({ ...f, [k]: w });

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (!f.titel.trim()) return;
      if (f.wiederholung !== "keine" && !f.faellig) {
        zeigeMeldung("Eine wiederkehrende Aufgabe braucht ein Fälligkeitsdatum.", true);
        return;
      }
      const daten = {
        titel: f.titel.trim(), beschreibung: f.beschreibung, prio: f.prio,
        faellig: f.faellig || null, zustaendig: f.zustaendig,
        wiederholung: f.wiederholung, ...bezugZuFeldern(f.bezug),
      };
      setzeLaeuft(true);
      const ok = await aendern(
        () => aufgabe ? api.aufgabeAendern(aufgabe.id, daten) : api.aufgabeAnlegen(daten),
        "Gespeichert");
      setzeLaeuft(false);
      if (ok) aufSchliessen();
    }}>
      <Sheet
        titel={aufgabe ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {aufgabe ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm(`„${aufgabe.titel}“ löschen?`)) return;
              void aendern(() => api.aufgabeLoeschen(aufgabe.id), "Gelöscht").then(aufSchliessen);
            }}>Löschen</button>
          ) : <span />}
          <div className="r">
            <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
            <button className="btn btn-primary" type="submit" disabled={laeuft}>
              {laeuft ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>}
      >
        <div className="form-grid">
          <TextFeld titel="Aufgabe *" voll wert={f.titel} aendern={setze("titel")}
            platzhalter="z. B. Zählerstände ablesen" />
          <TextFeld titel="Fällig bis" typ="date" wert={f.faellig} aendern={setze("faellig")} />
          <WahlFeld titel="Priorität" wert={f.prio} aendern={setze("prio")}
            optionen={ausRecord(PRIO_TEXT)} />
          <WahlFeld titel="Wiederholung" wert={f.wiederholung} aendern={setze("wiederholung")}
            optionen={ausRecord(WIEDERHOLUNGEN)} />
          <TextFeld titel="Zuständig" wert={f.zustaendig} aendern={setze("zustaendig")}
            platzhalter="Name" />
          <WahlFeld titel="Gehört zu" voll wert={f.bezug} aendern={setze("bezug")}
            optionen={bezugOptionen(bestand.objekte)} />
          <TextflaecheFeld titel="Notiz" wert={f.beschreibung} aendern={setze("beschreibung")} />
        </div>
        {f.wiederholung !== "keine" ? (
          <p className="hint">
            Beim Abhaken entsteht automatisch die nächste Aufgabe. Der Folgetermin
            bemisst sich am Fälligkeitsdatum, nicht am Tag des Abhakens — so wandert
            eine Jahreswartung nicht mit jeder Verspätung nach hinten.
          </p>
        ) : null}
      </Sheet>
    </form>
  );
}

export function AufgabenSeite() {
  const bestand = useBestand();
  const { aendern, zeigeMeldung } = useZustand();
  const [blatt, setzeBlatt] = useState<{ aufgabe: Aufgabe | null } | null>(null);
  const [filter, setzeFilter] = useState<"offen" | "erledigt" | "alle">("offen");

  const jetzt = heute();
  const gefiltert = bestand.aufgaben.filter((a) =>
    filter === "alle" ? true : filter === "offen" ? !a.erledigt : a.erledigt);
  const u = bestand.uebersicht;

  return (
    <>
      <SeitenKopf titel="Aufgaben"
        unterzeile={`${u.aufgabenOffen} offen · ${bestand.aufgaben.length - u.aufgabenOffen} erledigt`}
        knopf="+ Aufgabe" aufNeu={() => setzeBlatt({ aufgabe: null })} />

      <div className="kpi-grid">
        <Kpi titel="Offen" wert={String(u.aufgabenOffen)} unten="insgesamt" />
        <Kpi titel="Überfällig" wert={String(u.aufgabenUeberfaellig)}
          unten="Termin verstrichen" ton={u.aufgabenUeberfaellig > 0 ? "red" : undefined} />
        <Kpi titel="Diese Woche" wert={String(u.aufgabenDemnaechst)}
          unten="in den nächsten sieben Tagen"
          ton={u.aufgabenDemnaechst > 0 ? "orange" : undefined} />
      </div>

      <div className="san-head">
        <Filter wert={filter} aendern={setzeFilter} optionen={[
          ["offen", "Offen"], ["erledigt", "Erledigt"], ["alle", "Alle"]] as const} />
        <span className="sub" style={{ margin: 0 }}>{gefiltert.length} Einträge</span>
      </div>

      {gefiltert.length === 0 ? (
        <Leer titel={filter === "erledigt" ? "Noch nichts erledigt." : "Keine offenen Aufgaben."}>
          Wiederkehrendes wie Zählerstände oder Wartungen einmal anlegen — die
          Folgetermine entstehen beim Abhaken von selbst.
        </Leer>
      ) : gefiltert.map((a) => {
        const ueberfaellig = !a.erledigt && a.faellig && a.faellig < jetzt;
        return (
          <div className={`mangel ${a.erledigt ? "done" : ""}`} key={a.id}>
            <button className={`cb ${a.erledigt ? "on" : ""}`}
              title={a.erledigt ? "Wieder öffnen" : "Als erledigt markieren"}
              onClick={async () => {
                let bericht = "";
                const ok = await aendern(async () => {
                  const ergebnis: Portfolio = await api.aufgabeAbhaken(a.id, !a.erledigt);
                  const folge = ergebnis.folgeAufgabe?.folgeFaellig;
                  bericht = folge ? `Erledigt · nächster Termin ${datum(folge)}` : "";
                  return ergebnis;
                }, a.erledigt ? "Wieder geöffnet" : (a.wiederholung === "keine" ? "Erledigt" : undefined));
                if (ok && bericht) zeigeMeldung(bericht);
              }}>
              {a.erledigt ? "✓" : ""}
            </button>

            <div className="mangel-body">
              <div className="mangel-title">
                <span className="txt">{a.titel}</span>
                {a.erledigt
                  ? <span className="chip done">erledigt {a.erledigt_am ? datum(a.erledigt_am) : ""}</span>
                  : <span className={`chip ${a.prio}`}>{PRIO_TEXT[a.prio]}</span>}
                {a.wiederholung !== "keine"
                  ? <span className="chip moebel">↻ {WIEDERHOLUNGEN[a.wiederholung]}</span> : null}
                {ueberfaellig ? <span className="chip hoch">überfällig</span> : null}
              </div>
              <div className="mangel-meta">
                {bezugName(bestand.objekte, a.objekt_id, a.einheit_id)}
                {a.faellig ? ` · fällig ${datum(a.faellig)}` : ""}
                {a.zustaendig ? ` · ${a.zustaendig}` : ""}
                {a.beschreibung ? ` · ${a.beschreibung.slice(0, 80)}` : ""}
              </div>
            </div>

            <div className="mangel-right">
              <button className="btn btn-quiet btn-sm"
                onClick={() => setzeBlatt({ aufgabe: a })}>Bearbeiten</button>
            </div>
          </div>
        );
      })}

      {blatt ? <AufgabeBlatt aufgabe={blatt.aufgabe} aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}

// ----------------------------------------------------------------- Dokumente

function DokumentBlatt({ aufSchliessen }: { aufSchliessen: () => void }) {
  const bestand = useBestand();
  const { aendern, zeigeMeldung } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);
  const [gewaehlt, setzeGewaehlt] = useState<Vorgemerkt | null>(null);
  const [f, setzeF] = useState({
    titel: "", art: "Sonstiges", datum: "", gueltig_bis: "", notiz: "", bezug: "",
  });
  const setze = <K extends keyof typeof f>(k: K) => (w: typeof f[K]) => setzeF({ ...f, [k]: w });

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (!gewaehlt) { zeigeMeldung("Bitte zuerst eine Datei auswählen.", true); return; }
      const bezug = bezugZuFeldern(f.bezug);
      setzeLaeuft(true);
      const ok = await aendern(() => api.dokumentHochladen(gewaehlt.blob, gewaehlt.name, {
        titel: f.titel.trim() || gewaehlt.name,
        art: f.art, datum: f.datum, gueltig_bis: f.gueltig_bis, notiz: f.notiz,
        objekt_id: bezug.objekt_id ?? "", einheit_id: bezug.einheit_id ?? "",
      }), "Dokument abgelegt");
      setzeLaeuft(false);
      if (ok) aufSchliessen();
    }}>
      <Sheet
        titel="Dokument ablegen"
        aufSchliessen={aufSchliessen}
        fuss={<div className="r">
          <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
          <button className="btn btn-primary" type="submit" disabled={laeuft || !gewaehlt}>
            {laeuft ? "Lädt hoch …" : "Ablegen"}
          </button>
        </div>}
      >
        <div className="form-grid">
          <div className="fld-block">
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 6 }}>
              Datei *
            </div>
            {gewaehlt ? (
              <div className="anhang-reihe" style={{ marginBottom: 10, alignItems: "center" }}>
                {gewaehlt.blob.type.startsWith("image/")
                  ? <img className="anhang" src={gewaehlt.vorschau} alt="" />
                  : <span className="anhang-pdf">📄</span>}
                <span className="sub" style={{ margin: 0 }}>
                  {gewaehlt.name} · {dateigroesse(gewaehlt.blob.size)}
                </span>
                <button type="button" className="btn-xs"
                  onClick={() => setzeGewaehlt(null)}>Andere wählen</button>
              </div>
            ) : (
              <AnhangWaehlen aufAuswahl={setzeGewaehlt} />
            )}
          </div>

          <TextFeld titel="Titel" voll wert={f.titel} aendern={setze("titel")}
            platzhalter={gewaehlt ? gewaehlt.name : "z. B. Kaufvertrag K32"} />
          <WahlFeld titel="Art" wert={f.art} aendern={setze("art")}
            optionen={ausListe(DOKUMENT_ARTEN)} />
          <TextFeld titel="Datum des Dokuments" typ="date" wert={f.datum} aendern={setze("datum")} />
          <TextFeld titel="Gültig bis" typ="date" wert={f.gueltig_bis} aendern={setze("gueltig_bis")} />
          <WahlFeld titel="Gehört zu" wert={f.bezug} aendern={setze("bezug")}
            optionen={bezugOptionen(bestand.objekte)} />
          <TextflaecheFeld titel="Notiz" wert={f.notiz} aendern={setze("notiz")} zeilen={2} />
        </div>
        <p className="hint">
          „Gültig bis“ ist der eigentliche Nutzen gegenüber einem Ordner auf der
          Festplatte: Ablaufende Versicherungen und befristete Verträge fallen auf,
          bevor es zu spät ist.
        </p>
      </Sheet>
    </form>
  );
}

export function DokumenteSeite() {
  const bestand = useBestand();
  const { aendern } = useZustand();
  const [neu, setzeNeu] = useState(false);
  const [art, setzeArt] = useState("alle");

  const jetzt = heute();
  const u = bestand.uebersicht;
  const arten = ["alle", ...new Set(bestand.dokumente.map((d) => d.art))];
  const gefiltert = art === "alle"
    ? bestand.dokumente
    : bestand.dokumente.filter((d) => d.art === art);

  const zustand = (d: Dokument): ReactNode => {
    if (!d.gueltig_bis) return null;
    if (d.gueltig_bis < jetzt) return <span className="chip hoch">abgelaufen</span>;
    const in60 = new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);
    if (d.gueltig_bis <= in60) return <span className="chip mittel">läuft aus</span>;
    return null;
  };

  return (
    <>
      <SeitenKopf titel="Dokumente"
        unterzeile={`${u.dokumenteGesamt} abgelegt`}
        knopf="+ Dokument" aufNeu={() => setzeNeu(true)} />

      <div className="kpi-grid">
        <Kpi titel="Abgelegt" wert={String(u.dokumenteGesamt)} unten="insgesamt" />
        <Kpi titel="Abgelaufen" wert={String(u.dokumenteAbgelaufen)}
          unten="Gültigkeit verstrichen" ton={u.dokumenteAbgelaufen > 0 ? "red" : undefined} />
        <Kpi titel="Laufen aus" wert={String(u.dokumenteLaufenAus)}
          unten="in den nächsten 60 Tagen"
          ton={u.dokumenteLaufenAus > 0 ? "orange" : undefined} />
      </div>

      {arten.length > 2 ? (
        <div className="san-head">
          <div className="seg" style={{ margin: 0 }}>
            {arten.map((a) => (
              <button key={a} className={art === a ? "on" : ""} onClick={() => setzeArt(a)}>
                {a === "alle" ? "Alle" : a}
              </button>
            ))}
          </div>
          <span className="sub" style={{ margin: 0 }}>{gefiltert.length} Einträge</span>
        </div>
      ) : null}

      {gefiltert.length === 0 ? (
        <Leer titel="Keine Dokumente.">
          Kaufverträge, Policen, Rechnungen und Angebote — abgelegt hinter dem
          Login, nicht in einem offenen Ordner.
        </Leer>
      ) : gefiltert.map((d) => (
        <div className="row-card" key={d.id}>
          <div style={{ minWidth: 220 }}>
            <div className="row-title">
              <a className="linkish" style={{ fontSize: 15, fontWeight: 600 }}
                href={api.dateiAdresse(d.datei_id)} target="_blank" rel="noreferrer">
                {d.titel}
              </a>
              <span className="chip niedrig">{d.art}</span>
              {zustand(d)}
            </div>
            <div className="row-sub">
              {bezugName(bestand.objekte, d.objekt_id, d.einheit_id)}
              {d.datum ? ` · vom ${datum(d.datum)}` : ""}
              {d.gueltig_bis ? ` · gültig bis ${datum(d.gueltig_bis)}` : ""}
              {` · ${dateigroesse(d.groesse)}`}
              {d.notiz ? ` · ${d.notiz}` : ""}
            </div>
          </div>
          <div className="row-actions">
            <a className="btn btn-quiet btn-sm" href={api.dateiAdresse(d.datei_id)}
              target="_blank" rel="noreferrer">Öffnen</a>
            <button className="btn btn-danger btn-sm" onClick={() => {
              if (!confirm(`„${d.titel}“ löschen? Die Datei wird mit entfernt.`)) return;
              void aendern(() => api.dokumentLoeschen(d.id), "Dokument gelöscht");
            }}>Löschen</button>
          </div>
        </div>
      ))}

      {neu ? <DokumentBlatt aufSchliessen={() => setzeNeu(false)} /> : null}
    </>
  );
}

// ------------------------------------------------------------- Kommunikation

function NachrichtBlatt(
  { nachricht, aufSchliessen }: { nachricht: Nachricht | null; aufSchliessen: () => void },
) {
  const bestand = useBestand();
  const { aendern } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);
  const alleMaengel = bestand.objekte.flatMap((o) =>
    o.maengel.map((m) => [`mangel:${m.id}`, `${o.name} — ${m.beschreibung.slice(0, 50)}`] as const));

  const vorgabeBezug = nachricht?.mangel_id
    ? `mangel:${nachricht.mangel_id}`
    : bezugAusFeldern(nachricht?.objekt_id ?? null, nachricht?.einheit_id ?? null);

  const [f, setzeF] = useState({
    richtung: nachricht?.richtung ?? "ausgehend",
    kanal: nachricht?.kanal ?? "Telefon",
    partner: nachricht?.partner ?? "",
    betreff: nachricht?.betreff ?? "",
    inhalt: nachricht?.inhalt ?? "",
    zeitpunkt: (nachricht?.zeitpunkt ?? new Date().toISOString()).slice(0, 16),
    bezug: vorgabeBezug,
  });
  const setze = <K extends keyof typeof f>(k: K) => (w: typeof f[K]) => setzeF({ ...f, [k]: w });

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (!f.partner.trim()) return;
      const daten = {
        richtung: f.richtung, kanal: f.kanal, partner: f.partner.trim(),
        betreff: f.betreff, inhalt: f.inhalt,
        zeitpunkt: new Date(f.zeitpunkt).toISOString(),
        ...(f.bezug.startsWith("mangel:")
          ? { mangel_id: f.bezug.slice(7), objekt_id: null, einheit_id: null }
          : { ...bezugZuFeldern(f.bezug), mangel_id: null }),
      };
      setzeLaeuft(true);
      const ok = await aendern(
        () => nachricht ? api.nachrichtAendern(nachricht.id, daten) : api.nachrichtAnlegen(daten),
        "Gespeichert");
      setzeLaeuft(false);
      if (ok) aufSchliessen();
    }}>
      <Sheet
        titel={nachricht ? "Eintrag bearbeiten" : "Kontakt festhalten"}
        aufSchliessen={aufSchliessen}
        fuss={<>
          {nachricht ? (
            <button className="btn btn-danger" type="button" onClick={() => {
              if (!confirm("Diesen Eintrag löschen?")) return;
              void aendern(() => api.nachrichtLoeschen(nachricht.id), "Gelöscht").then(aufSchliessen);
            }}>Löschen</button>
          ) : <span />}
          <div className="r">
            <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
            <button className="btn btn-primary" type="submit" disabled={laeuft}>
              {laeuft ? "Speichert …" : "Speichern"}
            </button>
          </div>
        </>}
      >
        <div className="form-grid">
          <WahlFeld titel="Richtung" wert={f.richtung} aendern={setze("richtung")}
            optionen={[["ausgehend", "von uns"], ["eingehend", "an uns"]] as const} />
          <WahlFeld titel="Weg" wert={f.kanal} aendern={setze("kanal")} optionen={ausListe(KANAELE)} />
          <TextFeld titel="Gesprächspartner *" voll wert={f.partner} aendern={setze("partner")}
            platzhalter="z. B. Heizung Müller GmbH" />
          <TextFeld titel="Betreff" voll wert={f.betreff} aendern={setze("betreff")} />
          <TextFeld titel="Zeitpunkt" typ="datetime-local" wert={f.zeitpunkt}
            aendern={setze("zeitpunkt")} />
          <WahlFeld titel="Gehört zu" wert={f.bezug} aendern={setze("bezug")}
            optionen={[...bezugOptionen(bestand.objekte), ...alleMaengel]} />
          <TextflaecheFeld titel="Was wurde besprochen?" wert={f.inhalt}
            aendern={setze("inhalt")} zeilen={5} />
        </div>
        <p className="hint">
          Der Bezug zu einem Mangel ist der wichtigste: In sechs Monaten weiß sonst
          niemand mehr, was der Handwerker zugesagt hat.
        </p>
      </Sheet>
    </form>
  );
}

export function KommunikationSeite() {
  const bestand = useBestand();
  const [blatt, setzeBlatt] = useState<{ nachricht: Nachricht | null } | null>(null);
  const [richtung, setzeRichtung] = useState<"alle" | "eingehend" | "ausgehend">("alle");

  const gefiltert = richtung === "alle"
    ? bestand.nachrichten
    : bestand.nachrichten.filter((n) => n.richtung === richtung);

  const bezugText = (n: Nachricht): string => {
    if (n.mangel_id) {
      for (const o of bestand.objekte) {
        const m = o.maengel.find((x) => x.id === n.mangel_id);
        if (m) return `${o.name} · Mangel: ${m.beschreibung.slice(0, 45)}`;
      }
    }
    return bezugName(bestand.objekte, n.objekt_id, n.einheit_id);
  };

  return (
    <>
      <SeitenKopf titel="Kommunikation"
        unterzeile={`${bestand.nachrichten.length} festgehaltene Kontakte`}
        knopf="+ Kontakt" aufNeu={() => setzeBlatt({ nachricht: null })} />

      <div className="san-head">
        <Filter wert={richtung} aendern={setzeRichtung} optionen={[
          ["alle", "Alle"], ["ausgehend", "Von uns"], ["eingehend", "An uns"]] as const} />
        <span className="sub" style={{ margin: 0 }}>{gefiltert.length} Einträge</span>
      </div>

      {gefiltert.length === 0 ? (
        <Leer titel="Nichts festgehalten.">
          Telefonate und Absprachen mit Handwerkern und Mietern notieren — mit
          Bezug zum Mangel, damit später nachvollziehbar bleibt, was zugesagt war.
        </Leer>
      ) : gefiltert.map((n) => (
        <div className="row-card" key={n.id} style={{ alignItems: "flex-start" }}>
          <div style={{ minWidth: 240, flex: 1 }}>
            <div className="row-title">
              <span className={`chip ${n.richtung === "eingehend" ? "vermietbar" : "moebel"}`}>
                {n.richtung === "eingehend" ? "↓ an uns" : "↑ von uns"}
              </span>
              <b>{n.partner}</b>
              <span className="chip niedrig">{n.kanal}</span>
            </div>
            <div className="row-sub">
              {new Date(n.zeitpunkt).toLocaleString("de-DE",
                { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              {" · "}{bezugText(n)}
              {n.betreff ? ` · ${n.betreff}` : ""}
            </div>
            {n.inhalt ? (
              <div style={{
                marginTop: 8, whiteSpace: "pre-wrap", fontSize: 14, color: "var(--ink)",
                background: "rgba(0,0,0,.03)", borderRadius: "var(--radius-s)", padding: "10px 13px",
              }}>{n.inhalt}</div>
            ) : null}
          </div>
          <div className="row-actions">
            <button className="btn btn-quiet btn-sm"
              onClick={() => setzeBlatt({ nachricht: n })}>Bearbeiten</button>
          </div>
        </div>
      ))}

      {blatt ? <NachrichtBlatt nachricht={blatt.nachricht}
        aufSchliessen={() => setzeBlatt(null)} /> : null}
    </>
  );
}
